import { AiSkillScope, SkillTemplate } from '@seen/core/models/skillTemplate'
import { isOfType } from '@seen/core/utils'
import {
  Activity,
  DropdownMenuItem,
  H1Editor,
  HoverPractitioner,
  Loader,
  MoreDropdownMenu,
  PageLayout,
  ResourceFieldForm,
  Textarea,
  toast,
  useCurrentPractitioner,
  useIsReadOnly,
  usePatchMutation,
  useRead,
  VersionHistoryDialog,
} from '@seen/react'
import {
  Button,
  Separator,
  TypographyOverline,
  TypographySubtle,
} from '@seen/ui'
import {
  IconHistory,
  IconLoader2,
  IconSparkles,
  IconTrash,
} from '@tabler/icons-react'
import { type JSX, useCallback, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { z } from 'zod'
import { AiSkillActivity } from '#src/pages/ai-skills/AiSkillActivity.tsx'
import { useGenerateAiSkillMetadataMutation } from '#src/pages/ai-skills/hooks/useGenerateAiSkillMetadataMutation.ts'

const SKILLS_PATH = '/settings/skills'
const DESCRIPTION_MAX = 250
const SCOPE_OPTIONS: { scope: AiSkillScope; label: string }[] = [
  { scope: AiSkillScope.Personal, label: 'Personal' },
  { scope: AiSkillScope.Shared, label: 'Workspace' },
]

function FieldHeader({
  label,
  description,
  aside,
}: {
  label: string
  description: string
  aside?: JSX.Element
}): JSX.Element {
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="flex flex-col gap-0.5">
        <TypographySubtle className="font-medium">{label}</TypographySubtle>
        <p className="text-xs leading-relaxed text-muted-foreground">
          {description}
        </p>
      </div>
      {aside}
    </div>
  )
}

export function AiSkillPage(): JSX.Element {
  const { skillId } = useParams()
  const navigate = useNavigate()
  const isReadOnly = useIsReadOnly()
  const currentPractitioner = useCurrentPractitioner()
  const { data: skill, isLoading, isError } = useRead(SkillTemplate, skillId)
  const [isVersionHistoryOpen, setIsVersionHistoryOpen] = useState(false)

  const { mutate: patchSkill } = usePatchMutation<SkillTemplate>()
  const {
    mutateAsync: generateAiSkillMetadata,
    isPending: isGeneratingAiSkillMetadata,
  } = useGenerateAiSkillMetadataMutation()

  const handleGenerateMetadata = useCallback(async () => {
    if (!skill) {
      return
    }
    try {
      const metadata = await generateAiSkillMetadata(skill.content)
      patchSkill(
        {
          instance: skill,
          mutateFn: (draft) => {
            draft.title = metadata.title
            draft.description = metadata.description
          },
        },
        { onError: () => toast.error('Failed to save generated metadata') }
      )
    } catch {
      toast.error('Failed to generate skill metadata')
    }
  }, [skill, generateAiSkillMetadata, patchSkill])

  const handleRestoreVersion = useCallback(
    (restoredSkill: SkillTemplate) => {
      if (!skill) {
        return
      }

      patchSkill(
        {
          instance: skill,
          mutateFn: (draft) => {
            draft.title = restoredSkill.title
            draft.description = restoredSkill.description
            draft.scope = restoredSkill.scope
            draft.content = restoredSkill.content
          },
          patchOptions: {
            skipVersionCheck: true,
          },
        },
        {
          onSuccess: () => toast.success('Version restored'),
          onError: () => toast.error('Failed to restore version'),
        }
      )
    },
    [skill, patchSkill]
  )

  if (isLoading) {
    return <Loader />
  }

  if (isError) {
    return (
      <PageLayout>
        <div className="text-sm text-muted-foreground">
          Failed to load AI skill.
        </div>
      </PageLayout>
    )
  }

  if (!skill) {
    return <div>Skill not found</div>
  }

  // Tech reviewers can open and manage skills they do not own. Surface whose
  // skill this is so they don't mistake another user's skill for their own.
  // Use the profile-derived tech flag (not the override-aware perms hook) so
  // the warning tracks real write capability even under a group override.
  const skillAuthor = skill.resourceAuthor
  const isOthersSkill =
    currentPractitioner.isTech() &&
    skillAuthor?.reference !== currentPractitioner.reference.reference

  return (
    <PageLayout>
      <div className="mx-auto flex w-full max-w-(--container-prose) flex-col gap-10 py-16">
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <TypographyOverline>AI Skill</TypographyOverline>
            <MoreDropdownMenu>
              <DropdownMenuItem onSelect={() => setIsVersionHistoryOpen(true)}>
                <IconHistory className="size-4" />
                Version history
              </DropdownMenuItem>
              {!isReadOnly && (
                <DropdownMenuItem
                  className="text-destructive focus:text-destructive"
                  onSelect={() => {
                    patchSkill(
                      {
                        instance: skill,
                        mutateFn: (draft) => {
                          draft.status = 'retired'
                        },
                      },
                      {
                        onSuccess: () => {
                          void navigate(SKILLS_PATH)
                        },
                        onError: () => toast.error('Failed to delete AI skill'),
                      }
                    )
                  }}
                >
                  <IconTrash className="size-4" />
                  Delete skill
                </DropdownMenuItem>
              )}
            </MoreDropdownMenu>
          </div>
          <ResourceFieldForm
            instance={skill}
            valueFn={(s) => s.title}
            patchFn={(s, value) => {
              s.title = value
            }}
            className="self-start"
            debounceTyping
            render={({ field }) => (
              <H1Editor placeholder="Untitled skill" {...field} />
            )}
            valueSchema={z
              .string()
              .min(1, { message: 'Title must be at least 1 character' })}
          />
          {isOthersSkill &&
            skillAuthor &&
            isOfType(skillAuthor, 'Practitioner') && (
              <p className="mt-1 rounded-md border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
                You are viewing a skill owned by{' '}
                <HoverPractitioner practitioner={skillAuthor} /> · tech team
                edits affect their skill.
              </p>
            )}
        </div>

        <section className="flex flex-col gap-3">
          <FieldHeader
            label="Description"
            description="Helps people decide when to reach for this skill."
          />
          <ResourceFieldForm
            instance={skill}
            valueFn={(s) => s.description}
            patchFn={(s, value) => {
              s.description = value
            }}
            debounceTyping
            valueSchema={z.string().max(DESCRIPTION_MAX)}
            render={({ field }) => (
              <Textarea
                {...field}
                value={field.value ?? ''}
                maxLength={DESCRIPTION_MAX}
                placeholder="Describe when this skill should be used."
                className="min-h-24 resize-y leading-relaxed"
              />
            )}
          />
        </section>

        <section className="flex flex-col gap-3">
          <FieldHeader
            label="Scope"
            description="Choose whether this skill is only yours or visible to the workspace."
          />
          <ResourceFieldForm
            instance={skill}
            valueFn={(s) => s.scope}
            patchFn={(s, value) => {
              s.scope = value
            }}
            valueSchema={z.union([
              z.literal(AiSkillScope.Personal),
              z.literal(AiSkillScope.Shared),
            ])}
            render={({ field }) => (
              <div className="inline-flex w-fit rounded-lg border bg-muted/40 p-1">
                {SCOPE_OPTIONS.map(({ scope, label }) => (
                  <Button
                    key={scope}
                    type="button"
                    variant={field.value === scope ? 'default' : 'ghost'}
                    size="sm"
                    disabled={isReadOnly}
                    className="min-w-24"
                    onClick={() => field.onChange(scope)}
                  >
                    {label}
                  </Button>
                ))}
              </div>
            )}
          />
        </section>

        <section className="flex flex-col gap-3">
          <FieldHeader
            label="Content"
            description="Text-only instructions injected when the skill is selected."
            aside={
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="shrink-0 gap-2"
                disabled={
                  isReadOnly ||
                  isGeneratingAiSkillMetadata ||
                  skill.content.trim() === ''
                }
                onClick={handleGenerateMetadata}
              >
                {isGeneratingAiSkillMetadata ? (
                  <IconLoader2 className="size-4 animate-spin" />
                ) : (
                  <IconSparkles className="size-4" />
                )}
                Generate title and description
              </Button>
            }
          />
          <ResourceFieldForm
            instance={skill}
            valueFn={(s) => s.content}
            patchFn={(s, value) => {
              s.content = value
            }}
            debounceTyping
            valueSchema={z.string()}
            render={({ field }) => (
              <Textarea
                {...field}
                value={field.value ?? ''}
                placeholder="Tell Orbit AI how to behave when this skill is selected."
                className="min-h-72 resize-y py-2 font-mono leading-relaxed"
              />
            )}
          />
        </section>

        <Separator />
        <Activity resource={skill}>
          <AiSkillActivity skill={skill} />
        </Activity>
      </div>
      <VersionHistoryDialog
        open={isVersionHistoryOpen}
        onOpenChange={setIsVersionHistoryOpen}
        resource={skill}
        renderVersion={(version) => <SkillVersionPreview skill={version} />}
        canRestore={!isReadOnly}
        onRestoreVersion={handleRestoreVersion}
      />
    </PageLayout>
  )
}

function SkillVersionPreview({ skill }: { skill: SkillTemplate }): JSX.Element {
  return (
    <div className="flex flex-col gap-5 p-4">
      <div className="flex flex-col gap-2">
        <TypographyOverline>AI Skill</TypographyOverline>
        <div className="flex flex-col gap-1">
          <h2 className="text-xl leading-tight font-semibold">
            {skill.title || 'Untitled skill'}
          </h2>
          {skill.description && (
            <p className="text-sm leading-relaxed text-muted-foreground">
              {skill.description}
            </p>
          )}
        </div>
        <span className="text-xs text-muted-foreground">
          Scope:{' '}
          {skill.scope === AiSkillScope.Shared ? 'Workspace' : 'Personal'}
        </span>
      </div>
      <pre className="overflow-auto rounded-md bg-muted/40 p-3 font-mono text-xs leading-relaxed whitespace-pre-wrap text-foreground">
        {skill.content || 'No content'}
      </pre>
    </div>
  )
}
